import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useState } from 'react';
import { useAuth0 } from '@brighte/passwordless-authenticator';
import { FINANCIAL_PRODUCT } from '@brighte/validation';
import { Header } from '@components/Header';
import { Loader } from '@components/Loader/Loader';
import { datadogRum } from '@datadog/browser-rum';
import { ApplicationFormStatus, QuoteStatus as Status } from '@graphql/index';
import { usePrepop } from '@hooks';
import { Box } from '@spark-web/box';
import { Stack } from '@spark-web/stack';
import { getTrackingId, isJointApplicant } from '@utils';
import { ApplicationStatus } from '../';
import { useApplicationData, useStepperActions } from '../../provider';
import LandTitleEligibilityCheck from '../LandTitle/LandTitleEligibilityCheck';
import QuoteStatusPage from '../QuoteStatusPage/QuoteStatusPage';
import SubmittedApplication from '../SubmittedApplication/SubmittedApplication';

const FINANCE_STEP_INDEX = 1;

function FormPage() {
  const { isLoading, data: quote } = usePrepop();
  const [landTitleCompleted, setLandTitleCompleted] = useState(false);
  const { onNext } = useStepperActions();

  const submitApplication = useApplicationData('SubmitApplication');
  const isApplicationSubmitted = submitApplication?.status === ApplicationFormStatus.Submitted;
  const trackingId = getTrackingId();
  const { user, error: authError } = useAuth0();

  useEffect(() => {
    // setUser to dataDog for session details
    if (user) {
      datadogRum.setUser({
        trackingId,
        id: user?.id,
        applicationType: quote?.financialProduct.name,
      });
      datadogRum.startSessionReplayRecording();
    }
  }, [user, authError]);

  const flags = useFlags();
  const isEligibilityCheckEnabled = flags['enableEligiblityCheckForm'] ?? false;

  const isBhselEligibilityCheckAccessible = quote?.financialProduct?.id === FINANCIAL_PRODUCT.BHESL;

  // True only when the eligibility-check flow is the branch actually rendered
  // (mirrors the switch below). Used to scope layout tweaks to those screens.
  const isEligibilityFlow =
    !isLoading &&
    !isApplicationSubmitted &&
    quote?.status !== Status.COMPLETED &&
    isBhselEligibilityCheckAccessible &&
    !isJointApplicant() &&
    isEligibilityCheckEnabled &&
    !landTitleCompleted;

  const renderFormPage = () => {
    switch (true) {
      case isLoading:
        return <Loader />;
      case isApplicationSubmitted:
        return <ApplicationStatus />;
      case quote?.status === Status.COMPLETED:
        return <SubmittedApplication />;
      case isEligibilityFlow:
        return (
          <LandTitleEligibilityCheck
            onComplete={() => {
              setLandTitleCompleted(true);
              onNext(FINANCE_STEP_INDEX);
            }}
          />
        );
      default:
        return <QuoteStatusPage status={quote?.status} />;
    }
  };

  // Eligibility screens supply their own vertical rhythm, so on mobile we drop the
  // shared form-page padding that otherwise stacks extra whitespace above/below the
  // stepper. Every other screen (incl. the first application-form screen) is unchanged.
  const wrapperPaddingY = isEligibilityFlow ? { tablet: 'medium' as const } : 'medium';

  return (
    <>
      <Header financialProductId={quote?.financialProduct?.id ?? ''} />
      <Box paddingY={wrapperPaddingY}>
        <Box paddingY={wrapperPaddingY}>
          <Stack gap="xxlarge">{renderFormPage()}</Stack>
        </Box>
      </Box>
    </>
  );
}

export default FormPage;
